/* eslint-disable indent */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ORGANIZATION_TYPES, TIMEZONE_VN } from 'constant';
import PropTypes from 'prop-types';
import { getAcadamicLevelShortHand, getContactOrganizationName, is1593 } from 'utils/func';

import { getMe } from 'apis/users.api';
import HtmlViewer from 'components/HtmlViewer';
import { Tooltip } from 'primereact/tooltip';
import { useQuery } from 'react-query';
import { useGetPersonnelAdjustmentByProjectId } from 'utils/hooks/personnelAdjustments';
import DownloadScientificProfile from '../../DownloadScientificProfile';
import Warning from '../../Warning';
import ViewHistoricalPersonnelAdjustment from '../ViewHistoricalPersonnelAdjustment';
import DomesticExpertFields from './DomesticExpertFields';
import ExpenditureInfo from './ExpenditureInfo';
import InternationalExpertFields from './InternationalExpertFields';
import MainParticipantFields from './MainParticipantFields';
import OwnerFields from './OwnerFields';
import ParticipantFields from './ParticipantFields';
import RecommendedExpertsFields from './RecommendedExpertsFields';
import ScientificSecretaryFields from './ScientificSecretaryFields';
import TechnicianFields from './TechnicianFields';

export default function GeneralInfo({
  generalInfoData,
  expenditureData,
  handleDownloadFile,
  organizations,
  handleDownloadScientificProfileFile,
}) {
  // #region Data
  const { t } = useTranslation();
  const { state } = useLocation();
  const projectId = state?.projectId || null;

  const navigate = useNavigate();
  const template = useMemo(() => generalInfoData.template, [generalInfoData.template]);
  const partners = useMemo(
    () =>
      generalInfoData?.projectProposalOrganizations.filter(
        (org) => org.type === ORGANIZATION_TYPES.PARTNER
      ) ?? [],
    [generalInfoData?.projectProposalOrganizations]
  );

  const [viewHistoricalScientificProfileVisible, setViewHistoricalScientificProfileVisible] =
    useState(false);

  const { data: getMeResponse } = useQuery(['me'], getMe);

  const { data: personnelAdjustments, isLoading: isPersonnelAdjustmentsLoading } =
    useGetPersonnelAdjustmentByProjectId(projectId, {
      enabled: !!projectId,
    });
  // #endregion

  // #region Event
  // eslint-disable-next-line react/prop-types
  const formatIndex = (_, props) => props.rowIndex + 1;

  const formatFullname = (value) => (
    <span>
      {getAcadamicLevelShortHand(value.academicRank, value.academicDegree, t, true)}
      {value.fullname}
      {value?.contact?.user?.scientificProfileUrl ? (
        <DownloadScientificProfile
          contactId={value?.contact?.id || value?.contactId}
          contactName={value?.fullname}
          academicRank={value?.academicRank}
          academicDegree={value?.academicDegree}
          handleDownloadFile={handleDownloadScientificProfileFile}
        />
      ) : (
        <Warning
          message={t('projectProposal.general.researchHumanResources.noScientificProfileUrl')}
          tooltipTarget="researcher-scientific-profile-warning"
        />
      )}
      {value?.isAuthorized && (
        <>
          <i
            className="edit-icon pi pi-pencil border-primary-900 ml-2"
            data-pr-tooltip={t('projectProposal.general.researchHumanResources.fields.authority')}
            data-pr-position="right"
          />
          <Tooltip target=".edit-icon" />
        </>
      )}
    </span>
  );
  const formatGender = (value) => t(`userProfile.gender.${value?.gender}`);

  const formatOrganizationName = (value) =>
    getContactOrganizationName(
      organizations,
      {
        ...value?.contact,
        specificOrganizationName:
          value?.contact?.specificOrganizationName || value?.specificOrganizationName,
      },
      t
    );

  const handleNavigateToProposal = (proposalId) => {
    navigate(`/proposal/view/${proposalId}`);
  };
  // #endregion Event

  return (
    <div className="pb-2">
      <h4>{t('projectProposal.general.viewLabel')}</h4>
      {/* A1 */}
      <h4 className="mb-2">{t('projectProposal.general.projectNameAndType.label')}</h4>
      <div className="ml-5 text-lg">
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.vietnameseName')}:
          </div>
          <div className="flex-1">{generalInfoData?.vietnameseName}</div>
        </div>
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
          </div>
          <div className="flex-1">{generalInfoData?.englishName}</div>
        </div>
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.projectType')}:{' '}
          </div>
          <div className="flex-1">{t(`projectTypes.${generalInfoData?.projectType}`)}</div>
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.experimental')}:{' '}
          </div>
          <div className="flex-1">
            {generalInfoData?.isExperimental ? t('options.yes') : t('options.no')}
          </div>
        </div>
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.proposalName')}:{' '}
          </div>
          {/* eslint-disable-next-line no-undef, no-restricted-globals */}
          <div className="flex-1">
            {generalInfoData?.proposal?.vietnameseName &&
            generalInfoData?.proposal?.ownerId === getMeResponse?.data?.contact?.id ? (
              <span
                className="text-blue-500 cursor-pointer hover:underline"
                onClick={() => handleNavigateToProposal(generalInfoData?.proposal?.id)}
              >
                {generalInfoData?.proposal?.vietnameseName}
              </span>
            ) : (
              <span>
                {generalInfoData?.proposalName || generalInfoData?.proposal?.vietnameseName || ''}
              </span>
            )}
          </div>
        </div>
        {/* eslint-disable-next-line no-restricted-globals */}
        {((location?.state && location?.state?.pubProjectProposalName) ||
          // eslint-disable-next-line no-undef
          (generalInfoData?.pubProjectProposalId && generalInfoData?.pubProjectProposal)) && (
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.pubProposalVietnameName')}:{' '}
            </div>
            {/* eslint-disable-next-line no-undef, no-restricted-globals */}
            <div className="flex-1">
              {generalInfoData?.pubProjectProposal?.vietnameseName ||
                // eslint-disable-next-line no-restricted-globals
                location?.state?.pubProjectProposalName}
            </div>
          </div>
        )}
      </div>

      {/* A2 */}
      <h4>{t('projectProposal.general.majorsOrMajorsGroup.label')}</h4>
      {generalInfoData?.projectProposalResearchFields?.map((item, index) => (
        <div className="flex ml-5 text-lg mb-2" key={item?.researchField?.id || index}>
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.majorsOrMajorsGroup.prio.researchField')} {index + 1}:{' '}
          </div>
          <div className="w-10rem" style={{ marginRight: '150px' }}>
            {item?.researchField?.name}
          </div>
          <div className="font-semibold mr-2 ">
            {t('projectProposal.general.majorsOrMajorsGroup.researchLine')}:{' '}
          </div>
          <div className="flex-1">{item?.researchLine}</div>
        </div>
      ))}
      {generalInfoData?.recommendedExperts?.length > 0 && (
        <RecommendedExpertsFields
          recommendedExperts={generalInfoData?.recommendedExperts}
          isLoading={false}
        />
      )}

      {/* A3 */}
      <div className="text-lg mt-3">
        <h4>
          {t('projectProposal.general.researchType.label')} &{' '}
          {t('projectProposal.general.researchType.missionType')}
        </h4>
        <p className="flex ml-5">
          <div className="w-25rem">
            <span className="font-semibold">
              {t('projectProposal.general.researchType.researchType')}:{' '}
            </span>
            {generalInfoData?.researchType?.name}
          </div>
          <div className="ml-5">
            <span className="font-semibold">
              {t('projectProposal.general.researchType.missionType')}:{' '}
            </span>
            {t(`missionTypes.${generalInfoData?.missionType}`)}
          </div>
        </p>
      </div>

      {/* A4 */}
      <div className="text-lg mt-2">
        <h4>{t('projectProposal.general.researchTime.label')}</h4>
        <div className="flex mb-1 ml-5">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.researchTime.researchTime')}:
          </div>
          {generalInfoData?.implementationMonths}
        </div>
        <div className="flex mb-1 ml-5">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.researchTime.yearProposed')}:{' '}
          </div>
          {generalInfoData?.yearProposed}
        </div>
      </div>

      {/* A5 */}
      {!is1593(template) && (
        <ExpenditureInfo expenditureData={expenditureData} generalInfoData={generalInfoData} />
      )}

      {/* A6 */}
      <div className="flex text-lg mt-2 flex-column">
        <h4>{t('projectProposal.general.implementationResources.label')}</h4>
        <h4>{t('projectProposal.general.ownerSection.label')}</h4>
        <div className="flex ml-5 flex-column">
          <div className="flex flex-wrap">
            <div className="w-30rem mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.academicRank')}:{' '}
              </span>
              <span>
                {generalInfoData?.owner?.academicRank
                  ? t(`academicRank.${generalInfoData?.owner?.academicRank}`)
                  : ''}
              </span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.academicDegree')}:{' '}
              </span>
              <span>
                {generalInfoData?.owner?.academicDegree
                  ? t(`academicDegree.${generalInfoData?.owner?.academicDegree}`)
                  : ''}
              </span>
            </div>
          </div>
          <div className="mb-2">
            <span className="font-semibold">
              {t('projectProposal.general.ownerSection.owner.name')}:{' '}
            </span>
            <span>{generalInfoData?.owner?.fullname}</span>
          </div>
          <div className="mb-2">
            <span className="font-semibold">
              {t('projectProposal.general.ownerSection.owner.dob')}:{' '}
            </span>
            <span>
              {generalInfoData?.owner?.dob
                ? new Date(generalInfoData?.owner?.dob).toLocaleDateString('en-GB', {
                    timeZone: TIMEZONE_VN,
                  })
                : ''}
            </span>
          </div>
          <div className="flex flex-wrap">
            <div className="w-20rem mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.citizenId')}:{' '}
              </span>
              <span>{generalInfoData?.owner?.citizenId}</span>
            </div>
            <div className="w-15rem mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.dateOfIssue')}:{' '}
              </span>
              <span>
                {generalInfoData?.owner?.dateOfIssue
                  ? new Date(generalInfoData?.owner?.dateOfIssue).toLocaleDateString('en-GB', {
                      timeZone: TIMEZONE_VN,
                    })
                  : ''}
              </span>
            </div>
            <div className="w-40rem mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.placeOfIssue')}:{' '}
              </span>
              <span>{generalInfoData?.owner?.placeOfIssue}</span>
            </div>
          </div>
          <div className="mb-2">
            <span className="font-semibold">
              {t('projectProposal.general.ownerSection.owner.taxIdentificationNumber')}:{' '}
            </span>
            <span>{generalInfoData?.owner?.taxIdentificationNumber}</span>
          </div>
          <div className="flex flex-wrap">
            <div className="w-30rem mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.bankAccount')}:{' '}
              </span>
              <span>{generalInfoData?.owner?.bankAccount}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.bank')}:{' '}
              </span>
              <span>{generalInfoData?.owner?.bank}</span>
            </div>
          </div>
          <div className="flex flex-wrap">
            <div className="mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.workplace')}:{' '}
              </span>
              <span>{generalInfoData?.owner?.workplace}</span>
            </div>
          </div>
          <div className="flex flex-wrap">
            <div className="w-30rem mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.ownerSection.owner.mobile')}:{' '}
              </span>
              <span>{generalInfoData?.owner?.mobile}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">Email: </span>
              <span>{generalInfoData?.owner?.email}</span>
            </div>
          </div>
          {!is1593(template) && (
            <div className="mb-2">
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.ownerSection.owner.summarizedExperience')}:{' '}
                </span>
              </div>
              <HtmlViewer
                html={generalInfoData?.owner?.experience}
                className="border border-1 border-gray-300 p-4 border-round-lg bg-white"
              />
            </div>
          )}
        </div>
      </div>
      {/* scientificSecretarySection */}
      {is1593(template) && (
        <div className="flex text-lg mt-2 flex-column">
          <h4>{t('projectProposal.general.scientificSecretarySection.label')}</h4>
          <div className="flex ml-5 flex-column">
            <div className="flex flex-wrap">
              <div className="w-30rem mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.academicRank'
                  )}
                  :{' '}
                </span>
                <span>
                  {generalInfoData?.scientificSecretary?.academicRank
                    ? t(`academicRank.${generalInfoData?.scientificSecretary?.academicRank}`)
                    : ''}
                </span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.academicDegree'
                  )}
                  :{' '}
                </span>
                <span>
                  {generalInfoData?.scientificSecretary?.academicDegree
                    ? t(`academicDegree.${generalInfoData?.scientificSecretary?.academicDegree}`)
                    : ''}
                </span>
              </div>
            </div>
            <div className="mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.scientificSecretarySection.scientificSecretary.name')}:{' '}
              </span>
              <span>{generalInfoData?.scientificSecretary?.fullname}</span>
            </div>
            <div className="mb-2">
              <span className="font-semibold">
                {t('projectProposal.general.scientificSecretarySection.scientificSecretary.dob')}:{' '}
              </span>
              <span>
                {generalInfoData?.scientificSecretary?.dob
                  ? new Date(generalInfoData?.scientificSecretary?.dob).toLocaleDateString(
                      'en-GB',
                      {
                        timeZone: TIMEZONE_VN,
                      }
                    )
                  : ''}
              </span>
            </div>
            <div className="flex flex-wrap">
              <div className="w-20rem mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.citizenId'
                  )}
                  :{' '}
                </span>
                <span>{generalInfoData?.scientificSecretary?.citizenId}</span>
              </div>
              <div className="w-15rem mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.dateOfIssue'
                  )}
                  :{' '}
                </span>
                <span>
                  {generalInfoData?.scientificSecretary?.dateOfIssue
                    ? new Date(
                        generalInfoData?.scientificSecretary?.dateOfIssue
                      ).toLocaleDateString('en-GB', {
                        timeZone: TIMEZONE_VN,
                      })
                    : ''}
                </span>
              </div>
              <div className="w-40rem mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.placeOfIssue'
                  )}
                  :{' '}
                </span>
                <span>{generalInfoData?.scientificSecretary?.placeOfIssue}</span>
              </div>
            </div>
            <div className="mb-2">
              <span className="font-semibold">
                {t(
                  'projectProposal.general.scientificSecretarySection.scientificSecretary.taxIdentificationNumber'
                )}
                :{' '}
              </span>
              <span>{generalInfoData?.scientificSecretary?.taxIdentificationNumber}</span>
            </div>
            <div className="flex flex-wrap">
              <div className="w-30rem mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.bankAccount'
                  )}
                  :{' '}
                </span>
                <span>{generalInfoData?.scientificSecretary?.bankAccount}</span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.scientificSecretarySection.scientificSecretary.bank')}
                  :{' '}
                </span>
                <span>{generalInfoData?.scientificSecretary?.bank}</span>
              </div>
            </div>
            <div className="flex flex-wrap">
              <div className="mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.workplace'
                  )}
                  :{' '}
                </span>
                <span>{generalInfoData?.scientificSecretary?.workplace}</span>
              </div>
            </div>
            <div className="flex flex-wrap">
              <div className="w-30rem mb-2">
                <span className="font-semibold">
                  {t(
                    'projectProposal.general.scientificSecretarySection.scientificSecretary.mobile'
                  )}
                  :{' '}
                </span>
                <span>{generalInfoData?.scientificSecretary?.mobile}</span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">Email: </span>
                <span>{generalInfoData?.scientificSecretary?.email}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {is1593(template) && (
        <div className="mb-2">
          <div className="mb-2">
            <span className="font-semibold">
              {t('projectProposal.general.ownerSection.owner.summarizedExperience')}:{' '}
            </span>
          </div>
          <HtmlViewer
            html={generalInfoData?.owner?.experience}
            className="border border-1 border-gray-300 p-4 border-round-lg bg-white"
          />
        </div>
      )}

      {/* A7 */}
      <div className="flex text-lg mt-2 flex-column">
        <h4>{t('projectProposal.general.leadingAgency.label')}</h4>
        {generalInfoData?.projectProposalOrganizations
          .filter((org) => org.type === 'LEAD')
          .map((org, index) => (
            <div className="flex ml-5 flex-column" key={org?.id || index}>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.name')}:{' '}
                </span>
                <span>{org?.name}</span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.chief')}:{' '}
                </span>
                <span>{org?.chief}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.phone')}:{' '}
                  </span>
                  <span>{org?.phone}</span>
                </div>
                <div className="w-20rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.fax')}:{' '}
                  </span>
                  <span>{org?.fax}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.email')}:{' '}
                </span>
                <span>{org?.email}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.bankAccount')}:{' '}
                  </span>
                  <span>{org?.bankAccount}</span>
                </div>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.bank')}:{' '}
                  </span>
                  <span>{org?.bank}</span>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* A8 */}
      <div className="flex text-lg mt-2 flex-column">
        <h4>{t('projectProposal.general.partners.label')}</h4>
        {partners?.length > 0 ? (
          partners.map((org, index) => (
            <div className="flex ml-5 mb-2 flex-column" key={org?.id || index}>
              <div className="mb-2">
                <span className="font-bold">
                  {t('projectProposal.general.organization.name')} {index + 1}:{' '}
                </span>
                <span>{org?.name}</span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.partners.confirmationDocument')}:{' '}
                </span>
                {org?.partnerConfirmationUrl && (
                  <span
                    className="p-link text-primary underline"
                    onClick={() => handleDownloadFile(org?.partnerConfirmationUrl)}
                  >
                    {t('projectProposal.general.partners.attachment')}
                  </span>
                )}
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.chief')}:{' '}
                </span>
                <span>{org?.chief}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.phone')}:{' '}
                  </span>
                  <span>{org?.phone}</span>
                </div>
                <div className="w-20rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.fax')}:{' '}
                  </span>
                  <span>{org?.fax}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.address')}:{' '}
                </span>
                <span>{org?.address}</span>
              </div>
              <div
                className={
                  index === generalInfoData.projectProposalOrganizations.length - 2 ? '' : 'mb-3'
                }
              >
                <div>
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.partnerContribution')}:{' '}
                  </span>
                </div>
                <p>{org?.partnerContribution}</p>
              </div>
            </div>
          ))
        ) : (
          <span>({t('projectProposal.general.partners.noPartner')})</span>
        )}
      </div>

      {/* A9 */}
      <div className="flex align-items-center">
        <h4>{t('projectProposal.general.researchHumanResources.label')}</h4>
        {personnelAdjustments?.length > 0 && (
          <span
            className="download-compare-file pointer-events-auto ml-2 cursor-pointer"
            onClick={() => setViewHistoricalScientificProfileVisible(true)}
          >
            <i
              className="view-icon pi pi-eye"
              data-pr-tooltip={t('projectProposal.viewHistoricalPersonnelAdjustment.view')}
              data-pr-position="right"
              style={{ color: 'var(--cyan-600)', fontSize: 15 }}
            />
            <Tooltip target=".view-icon" />
          </span>
        )}
      </div>

      <OwnerFields
        owners={generalInfoData?.projectProposalContacts.filter((item) =>
          item?.projectRole?.code.toLowerCase().includes('owner')
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />

      <ScientificSecretaryFields
        scientificSecretaries={generalInfoData?.projectProposalContacts.filter((item) =>
          item?.projectRole?.code.toLowerCase().includes('secretary')
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />
      <MainParticipantFields
        mainParticipants={generalInfoData?.projectProposalContacts.filter((item) =>
          item?.projectRole?.code.toLowerCase().includes('main')
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />
      <ParticipantFields
        participants={generalInfoData?.projectProposalContacts.filter(
          (item) => item?.projectRole?.code.toLowerCase() === 'participant'
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />
      <TechnicianFields
        technicians={generalInfoData?.projectProposalContacts.filter(
          (item) =>
            item?.projectRole?.code.toLowerCase().includes('technician') ||
            item?.projectRole?.code.toLowerCase().includes('support')
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />
      <DomesticExpertFields
        experts={generalInfoData?.projectProposalContacts.filter((item) =>
          item?.projectRole?.code.toLowerCase().includes('domestic')
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />
      <InternationalExpertFields
        experts={generalInfoData?.projectProposalContacts.filter((item) =>
          item?.projectRole?.code.toLowerCase().includes('international')
        )}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />

      <ViewHistoricalPersonnelAdjustment
        visible={viewHistoricalScientificProfileVisible}
        setVisible={setViewHistoricalScientificProfileVisible}
        generalInfoData={personnelAdjustments}
        isLoading={isPersonnelAdjustmentsLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatOrganizationName={formatOrganizationName}
      />
    </div>
  );
}

GeneralInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  generalInfoData: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  expenditureData: PropTypes.object.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array.isRequired,
  handleDownloadScientificProfileFile: PropTypes.func.isRequired,
};
